import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { publicApi, formatApiErrorDetail } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, remove, setQty, total, clear, count } = useCart();
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await publicApi.post("/checkout/session", {
        items: items.map((it) => ({
          merch_id: it.merch_id || it.id, // fallback for pre-existing carts without merch_id
          size: it.size || null,
          quantity: it.qty,
        })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setBusy(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Checkout is temporarily unavailable");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        data-testid="cart-drawer"
        style={{ backgroundColor: "var(--ink-raised)", borderLeft: "1px solid var(--gold)", color: "var(--parchment)" }}
        className="w-full sm:max-w-md flex flex-col"
      >
        <SheetHeader>
          <SheetTitle className="font-caps text-gold flex items-center gap-2" style={{ fontSize: ".8rem" }}>
            <ShoppingBag size={16} /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div>
              <p className="font-display" style={{ fontSize: "1.4rem", color: "var(--parchment)" }}>Your cart is empty</p>
              <p className="mt-2" style={{ color: "var(--ash)" }}>Add label-branded pieces from the shop.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto no-scrollbar mt-4 space-y-4 pr-1">
              {items.map((it) => (
                <div key={it.id} className="flex gap-4 pb-4" style={{ borderBottom: "var(--rule)" }} data-testid={`cart-item-${it.id}`}>
                  <img src={it.image} alt={it.name} className="w-16 h-16 object-cover" style={{ borderRadius: "var(--radius)", border: "var(--rule)" }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-body truncate" style={{ color: "var(--parchment)" }}>{it.name}</p>
                    <p className="price-tag mt-1" style={{ fontSize: "1.1rem" }}>${it.price}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => setQty(it.id, it.qty - 1)} className="h-6 w-6 grid place-items-center" style={{ border: "var(--rule)" }} aria-label="Decrease"><Minus size={12} /></button>
                      <span style={{ fontSize: ".9rem" }}>{it.qty}</span>
                      <button onClick={() => setQty(it.id, it.qty + 1)} className="h-6 w-6 grid place-items-center" style={{ border: "var(--rule)" }} aria-label="Increase"><Plus size={12} /></button>
                      <button onClick={() => remove(it.id)} className="ml-auto text-rose" aria-label="Remove" data-testid={`cart-remove-${it.id}`}><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4 mt-2" style={{ borderTop: "1px solid var(--gold)" }}>
              <div className="flex items-center justify-between mb-4">
                <span className="font-caps" style={{ fontSize: ".72rem", color: "var(--ash)" }}>Subtotal</span>
                <span className="price-tag" style={{ fontSize: "1.5rem" }}>${total}</span>
              </div>
              <button
                className="btn-gold w-full justify-center"
                data-testid="checkout-btn"
                disabled={busy}
                onClick={checkout}
              >
                {busy ? "Redirecting…" : "Checkout"}
              </button>
              <button onClick={clear} className="w-full mt-3 font-caps" style={{ fontSize: ".62rem", color: "var(--ash)", background: "none", border: "none", cursor: "pointer" }}>
                Clear cart
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
